import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NumberInput, useGetList } from "react-admin";
import { useFormContext, useWatch, type UseFormSetValue } from "react-hook-form";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  computeVisibleSendTotal,
  isPreviewTruncated,
  shouldShowStalePreviewBanner,
} from "./outstanding-preview-state";
import { OUTSTANDING_PREVIEW_FETCH_LIMIT } from "./utils";

export type OutstandingRecipientRow = {
  phone?: string;
  memberItsNo?: string;
  memberRole?: string;
  recipientName?: string;
  itsNo?: string;
  name?: string;
  sabilId?: string;
  sabilType?: string;
  monthlyTakhmeenAmount?: number;
  takhmeenAmount?: number;
  paidBalance?: number;
  pendingBalance?: number;
  outstandingPercent?: number;
  asOfDate?: string;
  templateName?: string;
};

export type OutstandingRecipientColumn = {
  key: string;
  header: string;
  align?: "right";
  render: (row: OutstandingRecipientRow) => ReactNode;
  /**
   * Below the `sm` breakpoint, columns tagged "identity" are folded into one summary cell instead
   * of each getting their own column — a 12-column table doesn't fit a phone screen even with
   * horizontal scroll. Columns that matter for a send decision (Due/Paid/Pending/Outstanding %)
   * should stay untagged so they remain directly visible on mobile.
   */
  compactGroup?: "identity";
};

type NamedChoice = { id: string; name: string };

function itsChoiceToId(value: string | NamedChoice): string {
  if (typeof value === "string") {
    return value.trim();
  }
  return String(value.id ?? "").trim();
}

function itsChoiceLabel(value: string | NamedChoice): string {
  if (typeof value === "string") {
    return value;
  }
  return value.name;
}

type PreviewRecipientsResult = {
  data:
    | {
        data?: OutstandingRecipientRow[];
        total?: number;
        asOfDate?: string;
      }
    | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => unknown;
};

export type OutstandingRecipientPanelProps = {
  /** Fallback used before the form's `sabilType` field has a value (e.g. "CHULA", "MUTTAVATTEEN"). */
  sabilTypeDefault: string;
  /** `usePreviewSabilOutstandingRecipients` or `usePreviewMuttavatteenOutstandingRecipients`. */
  usePreviewRecipients: (params: {
    minOutstandingPercent?: number;
    itsIds?: string[];
    notifyMode?: string;
    sabilType?: string;
    limit?: number;
    offset?: number;
    enabled: boolean;
  }) => PreviewRecipientsResult;
  /** Sets `minOutstandingPercent`/`notifyMode` defaults on mount / when they're cleared. */
  applyFieldDefaults: (params: {
    minOutstandingPercent: number | undefined;
    notifyMode: string | undefined;
    setValue: UseFormSetValue<Record<string, unknown>>;
  }) => void;
  introText: (sabilType: string) => ReactNode;
  itsAutocompleteLabel: string;
  minPercentHelperText: string;
  /** Extra UI in the Audience filters card, after the ITS autocomplete (notify-mode radios, or a fixed-mode note). */
  renderNotifyModeSection: (params: {
    notifyMode: string | undefined;
    setValue: UseFormSetValue<Record<string, unknown>>;
    invalidatePreview: () => void;
  }) => ReactNode;
  /** The "(...)" clause in "N WhatsApp messages will be sent (...)". */
  describeSendMode: (notifyMode: string | undefined) => string;
  columns: (sabilType: string) => OutstandingRecipientColumn[];
  getRowKey: (row: OutstandingRecipientRow, index: number) => string;
  /** ITS id to report upward for a matched row — the rest (phone/name) is the same for every outstanding kind. */
  extractRowItsId: (row: OutstandingRecipientRow) => string | null | undefined;
  onPreviewCount?: (count: number) => void;
  onPreviewed?: (previewed: boolean) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
};

/**
 * Shared shell for the Sabil and Mutawatteen outstanding-reminder recipient panels — audience
 * filters (min outstanding %, optional ITS allowlist, notify mode), a Preview button, and a
 * paginated results table. The two callers differ only in: which preview endpoint they call, the
 * notify-mode UI/copy, and the result table's columns — everything else (debounced ITS search,
 * stale-preview banner, pagination) is identical and lives here once.
 */
export default function OutstandingRecipientPanel({
  sabilTypeDefault,
  usePreviewRecipients,
  applyFieldDefaults,
  introText,
  itsAutocompleteLabel,
  minPercentHelperText,
  renderNotifyModeSection,
  describeSendMode,
  columns,
  getRowKey,
  extractRowItsId,
  onPreviewCount,
  onPreviewed,
  onSelectionChange,
}: OutstandingRecipientPanelProps) {
  const { control, setValue } = useFormContext();
  const minOutstandingPercent = useWatch({ control, name: "minOutstandingPercent" }) as
    | number
    | undefined;
  const itsIds = useWatch({ control, name: "itsIds" }) as string[] | undefined;
  const notifyMode = useWatch({ control, name: "notifyMode" }) as string | undefined;
  const sabilType =
    (useWatch({ control, name: "sabilType" }) as string | undefined) ?? sabilTypeDefault;

  const [shouldFetch, setShouldFetch] = useState(false);
  const [hasPreviewedOnce, setHasPreviewedOnce] = useState(false);
  const [excludedItsIds, setExcludedItsIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [itsSearchText, setItsSearchText] = useState("");
  const debouncedItsSearchText = useDebouncedValue(itsSearchText, 300);

  const trimmedItsSearch = debouncedItsSearchText.trim();
  const { data: itsSearchRows = [], isLoading: itsSearchLoading } = useGetList(
    "itsdata",
    {
      filter: trimmedItsSearch ? { q: trimmedItsSearch } : {},
      pagination: { page: 1, perPage: 25 },
      sort: { field: "Full_Name", order: "ASC" },
    },
    { enabled: trimmedItsSearch.length >= 2 }
  );

  const itsChoices = useMemo<NamedChoice[]>(() => {
    const byId = new Map<string, NamedChoice>();
    for (const row of itsSearchRows) {
      const its = String(row.ITS_ID ?? "").trim();
      if (!its) continue;
      const name = String(row.Full_Name ?? "").trim();
      byId.set(its, { id: its, name: name ? `${its} · ${name}` : its });
    }
    for (const its of itsIds ?? []) {
      const id = String(its ?? "").trim();
      if (!id || byId.has(id)) continue;
      byId.set(id, { id, name: id });
    }
    return [...byId.values()];
  }, [itsSearchRows, itsIds]);

  useEffect(() => {
    applyFieldDefaults({ minOutstandingPercent, notifyMode, setValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minOutstandingPercent, notifyMode, setValue]);

  const selectionKey = useMemo(
    () =>
      JSON.stringify({
        minOutstandingPercent,
        itsIds,
        notifyMode,
        sabilType,
      }),
    [minOutstandingPercent, itsIds, notifyMode, sabilType]
  );

  const { data, isLoading, error, refetch } = usePreviewRecipients({
    minOutstandingPercent,
    itsIds,
    notifyMode,
    sabilType,
    limit: OUTSTANDING_PREVIEW_FETCH_LIMIT,
    offset: 0,
    enabled: shouldFetch,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const resolvedAsOfDate = data?.asOfDate;
  // The server always reports the true count of qualifying recipients, even when `rows` is
  // capped by OUTSTANDING_PREVIEW_FETCH_LIMIT — use it (not rows.length) so the "will be sent"
  // total stays correct even if a tenant's audience ever exceeds the fetch cap.
  const fullTotal = data?.total ?? 0;
  const isTruncated = isPreviewTruncated(rows.length, fullTotal);

  const isRowExcluded = useCallback(
    (row: OutstandingRecipientRow) => {
      const id = extractRowItsId(row);
      return !!id && excludedItsIds.has(id);
    },
    [excludedItsIds, extractRowItsId]
  );

  const visibleRows = useMemo(
    () => rows.filter((row) => !isRowExcluded(row)),
    [rows, isRowExcluded]
  );
  const visibleTotal = computeVisibleSendTotal(fullTotal, excludedItsIds.size);

  useEffect(() => {
    setShouldFetch(false);
    setExcludedItsIds(new Set());
    onPreviewed?.(false);
    onSelectionChange?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  useEffect(() => {
    setValue("excludeItsIds", [...excludedItsIds], { shouldDirty: true });
  }, [excludedItsIds, setValue]);

  useEffect(() => {
    if (error) {
      onPreviewed?.(false);
      onSelectionChange?.([]);
    } else if (shouldFetch && !isLoading) {
      onPreviewCount?.(visibleTotal);
      onPreviewed?.(visibleTotal > 0);

      const selectedRecipients = visibleRows
        .map((row) => {
          const phone = String(row.phone ?? "").trim();
          if (!phone) return null;
          return {
            ...row,
            phone,
            ITS_ID: String(extractRowItsId(row) ?? "").trim() || null,
            Full_Name: String(row.recipientName ?? row.name ?? "").trim() || null,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      onSelectionChange?.(selectedRecipients);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleTotal,
    isLoading,
    error,
    shouldFetch,
    visibleRows,
    onPreviewCount,
    onPreviewed,
    onSelectionChange,
  ]);

  const handlePreview = useCallback(() => {
    setShouldFetch(true);
    setHasPreviewedOnce(true);
    if (shouldFetch) {
      void refetch();
    }
  }, [shouldFetch, refetch]);

  const invalidatePreview = useCallback(() => {
    setShouldFetch(false);
    onPreviewed?.(false);
    onSelectionChange?.([]);
  }, [onPreviewed, onSelectionChange]);

  const selectedItsChoices = useMemo(
    () => itsChoices.filter((c) => (itsIds ?? []).includes(c.id)),
    [itsChoices, itsIds]
  );

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const allRowItsIds = useMemo(
    () => rows.map((row) => extractRowItsId(row)).filter((id): id is string => !!id),
    [rows, extractRowItsId]
  );
  const allExcluded = allRowItsIds.length > 0 && allRowItsIds.every((id) => excludedItsIds.has(id));

  const toggleAllExcluded = useCallback(() => {
    setExcludedItsIds((prev) => (prev.size > 0 ? new Set() : new Set(allRowItsIds)));
  }, [allRowItsIds]);

  const toggleRowExcluded = useCallback((id: string) => {
    setExcludedItsIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const resolvedColumns = useMemo(() => columns(sabilType), [columns, sabilType]);
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  const identityColumns = useMemo(
    () => resolvedColumns.filter((col) => col.compactGroup === "identity"),
    [resolvedColumns]
  );
  const visibleColumns = useMemo(
    () =>
      isCompact
        ? resolvedColumns.filter((col) => col.compactGroup !== "identity")
        : resolvedColumns,
    [isCompact, resolvedColumns]
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {introText(sabilType)}
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Audience filters
          </Typography>

          <Box sx={{ mb: 2, maxWidth: { md: "50%" } }}>
            <NumberInput
              source="minOutstandingPercent"
              label="Min outstanding %"
              min={0}
              max={100}
              step={1}
              helperText={minPercentHelperText}
              fullWidth
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              options={itsChoices}
              loading={itsSearchLoading}
              value={selectedItsChoices}
              getOptionLabel={(o) => itsChoiceLabel(o as string | NamedChoice)}
              isOptionEqualToValue={(a, b) =>
                itsChoiceToId(a as string | NamedChoice) ===
                itsChoiceToId(b as string | NamedChoice)
              }
              onInputChange={(_, value) => setItsSearchText(value)}
              onChange={(_, value) => {
                const next = [
                  ...new Set(
                    (value as Array<string | NamedChoice>).map(itsChoiceToId).filter(Boolean)
                  ),
                ];
                setValue("itsIds", next, { shouldDirty: true });
                invalidatePreview();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={itsAutocompleteLabel}
                  helperText="Optional. Limit to these ITS numbers (search directory or type an ITS and press Enter). Leave empty for all matching filters."
                />
              )}
            />
          </Box>

          {renderNotifyModeSection({ notifyMode, setValue, invalidatePreview })}
        </CardContent>
      </Card>

      <Button variant="contained" onClick={handlePreview} disabled={isLoading}>
        Preview recipients
      </Button>

      {shouldShowStalePreviewBanner(hasPreviewedOnce, shouldFetch) && (
        <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
          Filters changed — click Preview recipients to see the updated list.
        </Alert>
      )}

      {shouldFetch && isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading recipients…</Typography>
        </Box>
      )}

      {!!error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(error as Error).message || "Failed to preview recipients"}
        </Alert>
      )}

      {shouldFetch && !isLoading && !error && (
        <>
          <Alert severity={visibleTotal > 0 ? "success" : "info"} sx={{ mt: 2, py: 0.5 }}>
            <Typography variant="body2" component="span" fontWeight="bold">
              {visibleTotal}
            </Typography>{" "}
            WhatsApp messages will be sent ({describeSendMode(notifyMode)})
            {resolvedAsOfDate ? ` as of ${resolvedAsOfDate}` : ""}
            {excludedItsIds.size > 0
              ? ` — ${excludedItsIds.size} unchecked below won't be sent`
              : ""}
          </Alert>
          {isTruncated && (
            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
              Showing the first {rows.length.toLocaleString()} of {fullTotal.toLocaleString()}{" "}
              matching recipients below. The send total above already accounts for everyone — narrow
              with the ITS filter above if you need to check off someone outside this preview.
            </Alert>
          )}
          {rows.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: isCompact ? 300 : 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={excludedItsIds.size === 0}
                          indeterminate={excludedItsIds.size > 0 && !allExcluded}
                          onChange={toggleAllExcluded}
                        />
                      </TableCell>
                      {isCompact && identityColumns.length > 0 && <TableCell>Details</TableCell>}
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key} align={col.align}>
                          {col.header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((row, index) => {
                      const rowItsId = extractRowItsId(row);
                      const excluded = isRowExcluded(row);
                      return (
                        <TableRow key={getRowKey(row, index)} sx={{ opacity: excluded ? 0.5 : 1 }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={!excluded}
                              disabled={!rowItsId}
                              onChange={() => rowItsId && toggleRowExcluded(rowItsId)}
                            />
                          </TableCell>
                          {isCompact && identityColumns.length > 0 && (
                            <TableCell>
                              <Stack spacing={0.25}>
                                {identityColumns.map((col) => (
                                  <Typography key={col.key} variant="caption" display="block">
                                    <strong>{col.header}:</strong> {col.render(row)}
                                  </Typography>
                                ))}
                              </Stack>
                            </TableCell>
                          )}
                          {visibleColumns.map((col) => (
                            <TableCell key={col.key} align={col.align}>
                              {col.render(row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
